// Service de gestion avancée des sessions utilisateur
import ErrorHandler from './errorHandler.js';

const SESSION_KEY = 'user_session';
const VISITOR_KEY = 'visitor_id';
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes d'inactivité
const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001');

class SessionManager {
  constructor() {
    this.sessionId = null;
    this.visitorId = null;
    this.fingerprint = null;
    this.sessionData = null;
    this.activityTimer = null;
    this.heartbeatInterval = null;
    this.pageLoadTime = Date.now();
    this.isBot = this.detectBot();
    this.initPromise = null;
    this.isInitialized = false;

    this.initPromise = this.init();
  }

  async init() {
    try {
      // Générer ou récupérer l'identifiant du visiteur (persistant)
      this.visitorId = this.getOrCreateVisitorId();

      // Générer le fingerprint du navigateur
      this.fingerprint = await this.generateFingerprint();

      // Créer ou restaurer la session
      await this.initSession();

      // Démarrer le tracking d'activité
      this.startActivityTracking();

      // Démarrer le heartbeat (mise à jour régulière)
      this.startHeartbeat();

      // Écouter les événements de navigation
      this.setupEventListeners();

      this.isInitialized = true;
      console.log('✅ SessionManager initialisé', { sessionId: this.sessionId, visitorId: this.visitorId });
    } catch (error) {
      console.error('❌ Erreur initialisation SessionManager:', error);
      // Créer une session de secours
      this.sessionId = `fallback_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      this.visitorId = `fallback_visitor_${Date.now()}`;
      this.isInitialized = true;
    }
  }

  /**
   * Attendre que le SessionManager soit initialisé
   */
  async waitForInit() {
    if (this.isInitialized) {
      return true;
    }
    await this.initPromise;
    return this.isInitialized;
  }

  /**
   * Détection de bot basique
   */
  detectBot() {
    const botPatterns = [
      /bot/i, /crawler/i, /spider/i, /crawling/i, /slurp/i, /scraper/i,
      /headless/i, /phantom/i, /puppeteer/i, /selenium/i
    ];

    const ua = navigator.userAgent;
    const isBot = botPatterns.some(pattern => pattern.test(ua));

    // Détection supplémentaire
    const hasWebDriver = navigator.webdriver;
    const hasPlugins = navigator.plugins && navigator.plugins.length === 0;
    const hasLanguages = navigator.languages && navigator.languages.length === 0;

    return isBot || hasWebDriver || (hasPlugins && hasLanguages);
  }

  /**
   * Génère un identifiant unique de visiteur (persistant entre les sessions)
   */
  getOrCreateVisitorId() {
    let visitorId = localStorage.getItem(VISITOR_KEY);

    if (!visitorId) {
      visitorId = 'visitor_' + this.generateUUID();
      localStorage.setItem(VISITOR_KEY, visitorId);
    }

    return visitorId;
  }

  /**
   * Génère un fingerprint du navigateur pour identification unique
   */
  async generateFingerprint() {
    const components = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages?.join(',') || '',
      platform: navigator.platform,
      hardwareConcurrency: navigator.hardwareConcurrency || 0,
      deviceMemory: navigator.deviceMemory || 0,
      screenResolution: `${screen.width}x${screen.height}`,
      screenColorDepth: screen.colorDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),
      touchSupport: 'ontouchstart' in window,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      plugins: this.getPluginsList(),
      canvas: await this.getCanvasFingerprint(),
      webgl: this.getWebGLFingerprint(),
      fonts: await this.getFontFingerprint(),
    };

    // Générer un hash du fingerprint
    const str = JSON.stringify(components);
    const hash = await this.hashString(str);

    return {
      hash,
      components,
      confidence: this.calculateFingerprintConfidence(components)
    };
  }

  getPluginsList() {
    if (!navigator.plugins) return '';
    return Array.from(navigator.plugins)
      .map(p => p.name)
      .sort()
      .join(',');
  }

  async getCanvasFingerprint() {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 50;
      const ctx = canvas.getContext('2d');

      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('Analytics 🎨', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('Fingerprint', 4, 17);

      return canvas.toDataURL();
    } catch (e) {
      return '';
    }
  }

  getWebGLFingerprint() {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

      if (!gl) return '';

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      const vendor = debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : '';
      const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : '';

      return `${vendor}~${renderer}`;
    } catch (e) {
      return '';
    }
  }

  async getFontFingerprint() {
    const baseFonts = ['monospace', 'sans-serif', 'serif'];
    const testFonts = [
      'Arial', 'Verdana', 'Times New Roman', 'Courier New', 'Georgia',
      'Palatino', 'Garamond', 'Bookman', 'Comic Sans MS', 'Trebuchet MS',
      'Impact', 'Lucida Console'
    ];

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const text = 'mmmmmmmmmmlli';

    const baseSizes = baseFonts.map(baseFont => {
      ctx.font = `72px ${baseFont}`;
      return ctx.measureText(text).width;
    });

    const detectedFonts = testFonts.filter((font, idx) => {
      const baseFont = baseFonts[idx % baseFonts.length];
      ctx.font = `72px ${font}, ${baseFont}`;
      const size = ctx.measureText(text).width;
      return size !== baseSizes[idx % baseFonts.length];
    });

    return detectedFonts.join(',');
  }

  calculateFingerprintConfidence(components) {
    let score = 0;

    if (components.canvas) score += 30;
    if (components.webgl) score += 25;
    if (components.fonts) score += 20;
    if (components.plugins) score += 10;
    if (components.hardwareConcurrency > 0) score += 5;
    if (components.deviceMemory > 0) score += 5;
    if (components.touchSupport !== undefined) score += 5;

    return Math.min(score, 100);
  }

  async hashString(str) {
    // crypto.subtle n'est disponible qu'en contexte sécurisé (HTTPS / localhost)
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const encoder = new TextEncoder();
      const data = encoder.encode(str);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }
    // Fallback : hash simple (djb2) pour les contextes non-sécurisés (HTTP)
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash + str.charCodeAt(i)) >>> 0;
    }
    return 'fb_' + hash.toString(16).padStart(8, '0');
  }

  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Initialise ou restaure la session
   */
  async initSession() {
    const existingSession = this.loadSession();

    if (existingSession && !this.isSessionExpired(existingSession)) {
      // Restaurer la session existante
      this.sessionId = existingSession.sessionId;
      this.sessionData = existingSession;
      this.sessionData.pageViews++;
      this.sessionData.lastActivity = Date.now();

      await this.updateSession();
    } else {
      // Créer une nouvelle session
      await this.createNewSession();
    }
  }

  loadSession() {
    try {
      const data = localStorage.getItem(SESSION_KEY);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  isSessionExpired(session) {
    if (!session || !session.lastActivity) return true;
    return (Date.now() - session.lastActivity) > SESSION_TIMEOUT;
  }

  async createNewSession() {
    this.sessionId = 'session_' + this.generateUUID();

    // Déterminer si c'est un visiteur récurrent
    const isReturning = this.hasVisitedBefore();

    this.sessionData = {
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      fingerprint: this.fingerprint.hash,
      startTime: Date.now(),
      lastActivity: Date.now(),
      pageViews: 1,
      interactions: 0,
      scrollDepth: 0,
      isReturning,
      isBot: this.isBot,
      userAgent: navigator.userAgent,
      referrer: document.referrer || 'direct',
      landingPage: window.location.pathname,
      screenResolution: `${screen.width}x${screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      deviceType: this.getDeviceType(),
      browserName: this.getBrowserName(),
      osName: this.getOSName(),
      connectionType: this.getConnectionType(),
      pageLoadTime: this.pageLoadTime,
    };

    this.saveSession();
    await this.sendSessionToServer();
  }

  hasVisitedBefore() {
    // Vérifier si le visiteur a déjà visité le site
    const lastVisit = localStorage.getItem('last_visit');
    return !!lastVisit;
  }

  getDeviceType() {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  }

  getBrowserName() {
    const ua = navigator.userAgent;
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
    return 'Unknown';
  }

  getOSName() {
    const ua = navigator.userAgent;
    if (ua.includes('Win')) return 'Windows';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    return 'Unknown';
  }

  getConnectionType() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return connection ? connection.effectiveType : 'unknown';
  }

  saveSession() {
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(this.sessionData));
      localStorage.setItem('last_visit', Date.now().toString());
    } catch (e) {
      ErrorHandler.logError(e, 'SessionManager.saveSession');
    }
  }

  async updateSession() {
    this.sessionData.lastActivity = Date.now();
    this.sessionData.duration = Date.now() - this.sessionData.startTime;
    this.saveSession();

    // Mettre à jour sur le serveur (débounce)
    if (!this.updateTimeout) {
      this.updateTimeout = setTimeout(() => {
        this.sendSessionToServer();
        this.updateTimeout = null;
      }, 5000);
    }
  }

  async sendSessionToServer() {
    if (this.isBot) return; // Ne pas envoyer les sessions de bots

    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'session',
          data: {
            ...this.sessionData,
            duration: Math.floor((Date.now() - this.sessionData.startTime) / 1000),
            fingerprintConfidence: this.fingerprint.confidence,
          }
        }),
        signal: AbortSignal.timeout(10000),
      });

      if (response.ok) {
        console.log('✅ Session envoyée au serveur');
      }
    } catch (error) {
      ErrorHandler.logNetworkError(error, 'SessionManager.sendSessionToServer');
    }
  }

  /**
   * Tracking d'activité utilisateur
   */
  startActivityTracking() {
    // Réinitialiser le timer d'inactivité à chaque activité
    const resetActivity = () => {
      clearTimeout(this.activityTimer);
      this.updateSession();

      this.activityTimer = setTimeout(() => {
        console.log('⏰ Session expirée par inactivité');
        this.endSession();
      }, SESSION_TIMEOUT);
    };

    // Événements d'activité
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
      document.addEventListener(event, resetActivity, { passive: true });
    });

    resetActivity();
  }

  /**
   * Heartbeat - mise à jour régulière de la session
   */
  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      this.updateSession();
    }, 30000); // Toutes les 30 secondes
  }

  /**
   * Configuration des écouteurs d'événements
   */
  setupEventListeners() {
    // Avant de quitter la page
    window.addEventListener('beforeunload', () => {
      this.endSession();
    });

    // Visibilité de la page
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.updateSession();
      } else {
        this.sessionData.pageViews++;
        this.updateSession();
      }
    });

    // Tracking du scroll
    let maxScroll = 0;
    window.addEventListener('scroll', () => {
      const scrollPercent = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100;
      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
        this.sessionData.scrollDepth = Math.min(100, Math.floor(scrollPercent));
      }
    }, { passive: true });
  }

  /**
   * Enregistrer une interaction
   */
  recordInteraction(type, details = {}) {
    // Vérifier que sessionData existe et l'initialiser si nécessaire
    if (!this.sessionData) {
      console.warn('⚠️ sessionData non initialisé dans recordInteraction, attente d\'initialisation...');
      // Attendre que l'initialisation asynchrone se termine
      // En attendant, on ne traite pas l'interaction pour éviter l'erreur
      return;
    }

    this.sessionData.interactions = (this.sessionData.interactions || 0) + 1;
    this.updateSession();

    // Envoyer l'interaction au serveur
    this.sendInteraction(type, details);
  }

  async sendInteraction(type, details) {
    if (this.isBot) return;

    try {
      await fetch(`${API_BASE_URL}/api/analytics/interaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.sessionId,
          visitorId: this.visitorId,
          type,
          details,
          timestamp: Date.now(),
          page: window.location.pathname,
        }),
        signal: AbortSignal.timeout(5000),
      });
    } catch (error) {
      // Silencieux pour ne pas perturber l'expérience utilisateur
    }
  }

  /**
   * Terminer la session
   */
  endSession() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
    }

    this.sessionData.endTime = Date.now();
    this.sessionData.duration = this.sessionData.endTime - this.sessionData.startTime;
    this.saveSession();
    this.sendSessionToServer();
  }

  /**
   * Getters publics
   */
  getSessionId() {
    return this.sessionId;
  }

  getVisitorId() {
    return this.visitorId;
  }

  getFingerprint() {
    return this.fingerprint;
  }

  getSessionData() {
    return { ...this.sessionData };
  }

  isNewVisitor() {
    return !this.sessionData?.isReturning;
  }
}

// Export singleton
const sessionManager = new SessionManager();
export default sessionManager;

